// Lazy, memoized Amplify v6 data client (issue #143).
//
// WHY THIS EXISTS -- the root-cause fix for the deployed-bundle
// `Amplify has not been configured` regression:
//
//   Earlier the three data-layer consumers (DataContextProvider.tsx,
//   pages/Applicant.tsx, pages/TeacherView.tsx) each ran
//
//       const client = generateClient();   // <-- at MODULE-EVAL time
//
//   at their own module top. That couples correctness to the order the
//   bundler evaluates modules: `generateClient()` snapshots the Amplify
//   singleton's config at call time, so if any of those modules evaluate
//   before `amplify-init.ts` has run `Amplify.configure(...)`, the client
//   is built off an empty config (`isConfigured = false`). The
//   `index.tsx` "import amplify-init first" guard makes that order correct
//   *in source*, but the Amplify SDK's own source warns bundlers may
//   reorder it (quoted in `amplify-init.ts`'s header). In the production
//   webpack bundle the configure call lost the race, `client.models.
//   ProdApplicant.list()` returned `{ data: null, errors: [...] }`, and the
//   downstream `data.map(item => item.tags ...)` crashed with
//   "Cannot read properties of null (reading 'tags')" (MR !42 operator
//   notes `3374166197`, `3380435490`, `3451188970`).
//
//   `Amplify.configure` running first (import-order) is necessary but NOT
//   sufficient, because the bundler reorder defeats it. The robust fix is
//   to never call `generateClient()` at module-eval time at all. This
//   helper constructs the client lazily on first use -- which, for every
//   consumer, is inside an async data-fetch invoked from a React effect or
//   event handler, i.e. always strictly after `Amplify.configure(...)` has
//   run at app bootstrap. Module evaluation order is then irrelevant.
//
//   The client is memoized so we keep the prior "one client per process"
//   behavior (avoids re-building the models proxy on every fetch).
//
// Typed with `<Schema>` (issue #146) now that viewer-client/ is on
// TypeScript 5: `getClient().models.<Model>.list/get/...` return the
// schema-derived row shapes instead of `any`. The generic was dropped in
// #143 sub-step 4 only because react-scripts@5.0.1 pinned TS 4.9.5, which
// can't parse `@aws-amplify/data-schema`'s TS 5 const-generic `.d.ts`.
import { generateClient } from "aws-amplify/api";
import type { Schema } from "../../amplify/data/resource";

type DataClient = ReturnType<typeof generateClient<Schema>>;

let cachedClient: DataClient | null = null;

/**
 * Returns the process-wide Amplify v6 data client, constructing it on the
 * first call and caching it thereafter. Call this at use-time (inside the
 * async data loaders), never at module scope, so the client is always
 * built after `Amplify.configure(...)` has run -- regardless of how the
 * bundler orders module evaluation.
 */
export const getClient = (): DataClient => {
  if (cachedClient === null) {
    cachedClient = generateClient<Schema>();
  }
  return cachedClient;
};
